// Service API pour communiquer avec le backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.refreshPromise = null;
    this.accessToken = sessionStorage.getItem('token') || null;
    this.refreshToken = sessionStorage.getItem('refreshToken') || null;
  }

  setSession({ token, refreshToken, user }) {
    this.accessToken = token || null;
    this.refreshToken = refreshToken || null;

    if (this.accessToken) sessionStorage.setItem('token', this.accessToken);
    else sessionStorage.removeItem('token');

    if (this.refreshToken) sessionStorage.setItem('refreshToken', this.refreshToken);
    else sessionStorage.removeItem('refreshToken');

    if (user) {
      sessionStorage.setItem('user', JSON.stringify(user));
      localStorage.removeItem('user');
    }
  }

  clearSession(emitEvent = true) {
    this.accessToken = null;
    this.refreshToken = null;
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    if (emitEvent && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('funtoa:auth-invalidated'));
    }
  }

  onAuthInvalidated(listener) {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener('funtoa:auth-invalidated', listener);
    return () => window.removeEventListener('funtoa:auth-invalidated', listener);
  }

  async tryRefreshToken() {
    const currentRefreshToken = this.refreshToken || sessionStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      this.clearSession();
      return false;
    }

    if (!this.refreshPromise) {
      this.refreshPromise = this.request('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
        skipAuth: true,
        skipAuthRefresh: true
      })
        .then((response) => {
          const newToken = response?.data?.token;
          const rotatedRefreshToken = response?.data?.refreshToken;
          if (!response?.success || !newToken || !rotatedRefreshToken) {
            this.clearSession();
            return false;
          }

          this.setSession({
            token: newToken,
            refreshToken: rotatedRefreshToken,
            user: (() => {
              const userRaw = sessionStorage.getItem('user');
              return userRaw ? JSON.parse(userRaw) : null;
            })()
          });
          return true;
        })
        .catch(() => {
          this.clearSession();
          return false;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  // Méthode générique pour les requêtes
  async request(endpoint, options = {}) {
    let url = `${this.baseURL}${endpoint}`;
    const {
      params,
      headers: customHeaders = {},
      skipAuth = false,
      skipAuthRefresh = false,
      ...fetchOptions
    } = options;
    const token = skipAuth ? null : (this.accessToken || sessionStorage.getItem('token'));

    if (params && typeof params === 'object') {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        url += `${url.includes('?') ? '&' : '?'}${queryString}`;
      }
    }

    const config = {
      method: fetchOptions.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...customHeaders,
      },
      credentials: 'include', // Important pour CORS avec credentials
      ...fetchOptions,
    };
    const hasCustomSiteHeader = Object.keys(customHeaders).some(
      (headerKey) => String(headerKey).toLowerCase() === 'x-site-id'
    );
    if (!skipAuth && !hasCustomSiteHeader) {
      const selectedClinicalSiteId = sessionStorage.getItem('funtoa_clinical_work_site_id');
      if (selectedClinicalSiteId) {
        config.headers['x-site-id'] = selectedClinicalSiteId;
      }
    }

    try {
      const response = await fetch(url, config);
      
      // Vérifier si la réponse est JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }

      if (!response.ok) {
        if (
          response.status === 401 &&
          !skipAuthRefresh &&
          !endpoint.includes('/auth/login') &&
          !endpoint.includes('/auth/refresh')
        ) {
          const refreshed = await this.tryRefreshToken();

          if (refreshed) {
            return this.request(endpoint, {
              ...options,
              skipAuthRefresh: true
            });
          }
        }

        // Gestion spécifique des erreurs HTTP
        switch (response.status) {
          case 400: {
            // Corps JSON fiable (les en-têtes custom ne sont pas toujours lisibles en fetch cross-origin sans CORS expose)
            const loginBuildFromBody = data.authLoginBuild;
            const loginBuildFromHeader = response.headers.get('X-Funtoa-Auth-Login');
            const loginValidatorOk =
              loginBuildFromBody === 'validateLogin' ||
              loginBuildFromHeader === 'validateLogin';
            if (endpoint.includes('/auth/login') && !loginValidatorOk) {
              const technicalMessage =
                'Le serveur API sur ce port ne semble pas à jour (réponse 400 sans marqueur de validation connexion attendu). ' +
                'Arrêtez tous les processus Node qui écoutent sur ce port, ouvrez un terminal dans le dossier « backend » du projet, puis relancez : npm start';
              throw new Error(
                process.env.NODE_ENV === 'development'
                  ? technicalMessage
                  : 'Impossible de se connecter pour le moment. Veuillez réessayer.'
              );
            }
            const errs = data.errors;
            if (Array.isArray(errs) && errs.length) {
              const parts = errs.map((e) => {
                if (typeof e === 'string') return e;
                if (e.msg) return e.msg;
                if (e.message) return e.message;
                if (Array.isArray(e.nestedErrors) && e.nestedErrors.length) {
                  return e.nestedErrors.map((n) => n.msg || n.message).filter(Boolean).join(' ');
                }
                return JSON.stringify(e);
              });
              throw new Error(parts.filter(Boolean).join(' '));
            }
            throw new Error(data.message || 'Données invalides.');
          }
          case 401:
            // Pour la connexion, c'est probablement des identifiants incorrects
            if (endpoint.includes('/auth/login')) {
              throw new Error(data.message || 'Identifiant ou mot de passe incorrect.');
            } else {
              throw new Error('Session expirée. Veuillez vous reconnecter.');
            }
          case 403:
            throw new Error(data.message || 'Accès refusé. Permissions insuffisantes.');
          case 404:
            throw new Error('Ressource non trouvée.');
          case 409: {
            // Conflit métier (règle business violée, ex: tentative de
            // suppression d'une entité référencée ailleurs). Le backend
            // est attendu pour fournir un message clair.
            const conflictError = new Error(data.message || 'Action refusée par le serveur.');
            Object.keys(data || {}).forEach((key) => {
              if (key !== 'success' && key !== 'message' && key !== 'errors') {
                conflictError[key] = data[key];
              }
            });
            throw conflictError;
          }
          case 422:
            // Affichage des erreurs de validation
            const validationErrors = data.errors || [];
            const errorMessage = data.message || 'Données invalides.';
            if (validationErrors.length > 0) {
              throw new Error(`${errorMessage}: ${validationErrors.join(', ')}`);
            }
            throw new Error(errorMessage);
          case 429:
            // Gestion du rate limiting
            const retryAfter = data.retryAfter || 60;
            const error429 = new Error(data.message || 'Trop de requêtes. Veuillez patienter.');
            error429.retryAfter = retryAfter;
            error429.isRateLimited = true;
            throw error429;
          case 500:
            throw new Error(
              data.message ||
                data.debug ||
                'Erreur du serveur. Veuillez réessayer plus tard.'
            );
          default:
            throw new Error(data.message || `Erreur HTTP: ${response.status}`);
        }
      }

      return data;
    } catch (error) {
      console.error('Erreur API:', error);
      
      // Gestion des erreurs de réseau
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Erreur de connexion au serveur. Vérifiez votre connexion internet.');
      }
      
      throw error;
    }
  }

  // Méthodes GET
  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  // Méthodes POST
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Méthodes PUT
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Méthodes PATCH
  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Méthodes DELETE
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Service d'authentification
export const authService = {
  // Connexion
  /**
   * @param {string} identifier E-mail ou nom d'utilisateur (champ unique côté UI)
   */
  async login(identifier, password) {
    try {
      const trimmed = String(identifier ?? '').trim();
      const response = await apiService.post('/auth/login', {
        identifier: trimmed,
        password
      });
      
      if (response.success) {
        apiService.setSession({
          token: response.data.token,
          refreshToken: response.data.refreshToken,
          user: response.data.user
        });
        
        return {
          success: true,
          user: response.data.user,
          token: response.data.token
        };
      } else {
        return {
          success: false,
          message: response.message || 'Erreur de connexion'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  // Déconnexion
  async logout() {
    const currentRefreshToken = apiService.refreshToken || sessionStorage.getItem('refreshToken');
    try {
      await apiService.post('/auth/logout', {
        ...(currentRefreshToken ? { refreshToken: currentRefreshToken } : {})
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      apiService.clearSession();
    }
  },

  // Rafraîchir le token
  async refreshToken() {
    try {
      const refreshed = await apiService.tryRefreshToken();

      if (!refreshed) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      return sessionStorage.getItem('token');
    } catch (error) {
      // Si le refresh échoue, déconnecter l'utilisateur
      this.logout();
      throw error;
    }
  },

  // Obtenir le profil utilisateur
  async getProfile() {
    try {
      const response = await apiService.get('/auth/profile');
      // Retourner user et sites si présents
      return {
        user: response?.data?.user || null,
        sites: response?.data?.sites || []
      };
    } catch (error) {
      throw error;
    }
  },

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    const token = sessionStorage.getItem('token');
    const user = sessionStorage.getItem('user');
    return !!(token && user);
  },

  // Obtenir l'utilisateur actuel
  getCurrentUser() {
    const user = sessionStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Obtenir le token
  getToken() {
    return sessionStorage.getItem('token');
  }
};

// Instance du service API
const apiService = new ApiService();

export default apiService;
